import { stringify } from "querystring"

import { useDispatch } from "react-redux"
import { useQuery } from "@tanstack/react-query"

import {
    GET_CHAT_CLIENT_URL,
    GET_CHAT_PSY_URL,
    GET_CLIENT_LIST_URL,
    GET_CLIENT_TOKEN_URL,
    GET_PSY_LIST_URL,
    GET_PSY_TOKEN_URL,
    LS_CLIENT_TOKEN,
    LS_PSY_TOKEN,
} from "./constants"
import { ChatStorage } from "./../utils/ChatStorage"
import { Chat, ChatTokenResponse, Message, MessageStatus } from "./../utils/interfaces"
import { httpClientChat } from "./../utils/httpClient"

import { ROLES, RoleType } from "utils/constants"
import { httpClient } from "utils/infrastructure/httpClient"
import { isValidRole } from "utils/isValidRole"
import { setChats, setIsLoading, setMessages, setRole, setToken } from "utils/store/slices"
import { AppDispatch, RootState } from "utils/store/store"

export function setChatRole(role: RoleType) {
    return (dispatch: AppDispatch) => {
        ChatStorage.set({ role })
        dispatch(setRole(role))
    }
}

export function getChatRole(defaultRole: RoleType) {
    return (dispatch: AppDispatch) => {
        const role = ChatStorage.get()?.role

        if (!isValidRole(role)) {
            dispatch(setRole(defaultRole))
            ChatStorage.set({ role: defaultRole })
            return
        }

        dispatch(setRole(role))
        ChatStorage.set({ role })
    }
}

export function getChatToken(role: RoleType, fromLs = true) {
    return async (dispatch: AppDispatch) => {
        const lsTokenTitle = role === ROLES.CLIENT ? LS_CLIENT_TOKEN : LS_PSY_TOKEN

        if (fromLs) {
            const lsToken = ChatStorage.get()?.[lsTokenTitle]
            if (lsToken) {
                dispatch(setToken(lsToken))
                return lsToken
            }
        }

        // Fetch token from API
        const response = await httpClient.get<ChatTokenResponse>(
            role === ROLES.CLIENT ? GET_CLIENT_TOKEN_URL : GET_PSY_TOKEN_URL
        )
        const token = response.token

        // Save token to localStorage and store
        ChatStorage.set({ [lsTokenTitle]: token })
        dispatch(setToken(token))

        return token
    }
}

export function getChatList(role: RoleType) {
    return async (dispatch: AppDispatch) => {
        try {
            const response = await fetchChatList(role)
            dispatch(setChats(response))
            dispatch(setIsLoading(false))
            return response
        } catch (e) {
            console.log(e)
        }
    }
}

export async function fetchChatList(role: RoleType): Promise<Chat[]> {
    try {
        return await httpClient.get<Chat[]>(
            role === ROLES.CLIENT ? GET_CLIENT_LIST_URL : GET_PSY_LIST_URL
        )
    } catch (e) {
        console.log(e)
        throw e
    }
}

export function useChatList(role: RoleType) {
    const dispatch = useDispatch<AppDispatch>()

    return useQuery({
        queryKey: ["chatList", role],
        queryFn: () => fetchChatList(role),
        onSuccess: (data: Chat[]) => {
            dispatch(setChats(data))
        },
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchInterval: 60000,
        refetchIntervalInBackground: true,
    })
}

export function createChatWithNewMessage(chatId: number, role: RoleType) {
    return async (dispatch: AppDispatch, getState: () => RootState) => {
        try {
            const url =
                role === ROLES.CLIENT
                    ? `${GET_CLIENT_LIST_URL}?chat_id=${chatId}`
                    : `${GET_PSY_LIST_URL}?chat_id=${chatId}`

            const chat = await httpClient.get<Chat>(url)
            const updatedChat = {
                ...chat,
                unreadCount: 1,
                lastMessage: {
                    timestamp: new Date().toISOString(),
                    viewed: false,
                    userId: chat.userId,
                },
            }
            const currentChats = getState().chat.chats
            dispatch(setChats([...currentChats, updatedChat]))
        } catch (e) {
            console.log(e)
        }
    }
}

export function getChatId(role: RoleType, userId: string) {
    return async (dispatch: AppDispatch) => {
        try {
            dispatch(setIsLoading(true))
            const response = await httpClient.get<{ chatId: string }>(
                `${role === ROLES.CLIENT ? GET_CHAT_CLIENT_URL : GET_CHAT_PSY_URL}?${stringify({ target_hash: userId })}`
            )
            dispatch(setIsLoading(false))
            return response.chatId
        } catch (e) {
            dispatch(setIsLoading(false))
        }
    }
}

export function getMessages(token: string, chatId: number) {
    return async (dispatch: AppDispatch) => {
        try {
            const response = await httpClientChat.get<Message[]>(`chats/${chatId}/messages`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            })

            const messages = response.data.map((message) => ({
                ...message,
                status: message.viewed ? MessageStatus.VIEWED : MessageStatus.CREATED,
            }))

            dispatch(setMessages(messages))
        } catch (e) {
            console.log(e)
        }
    }
}
